// src/utils/auditService.js
// Audit logging service for tracking user actions

import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  startAfter,
} from "firebase/firestore";
import { getDeviceInfo, getLocationInfo } from "./sessionService";

/**
 * Audit action types
 */
export const AUDIT_ACTIONS = {
  // Authentication
  LOGIN: "login",
  LOGOUT: "logout",
  LOGIN_FAILED: "login_failed",
  PASSWORD_CHANGED: "password_changed",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",

  // Users & Team
  USER_CREATED: "user_created",
  USER_UPDATED: "user_updated",
  USER_DELETED: "user_deleted",
  EMPLOYEE_INVITED: "employee_invited",
  EMPLOYEE_REMOVED: "employee_removed",
  ROLE_CHANGED: "role_changed",

  // Customers
  CUSTOMER_CREATED: "customer_created",
  CUSTOMER_UPDATED: "customer_updated",
  CUSTOMER_DELETED: "customer_deleted",

  // Invoices
  INVOICE_CREATED: "invoice_created",
  INVOICE_UPDATED: "invoice_updated",
  INVOICE_DELETED: "invoice_deleted",
  INVOICE_SENT: "invoice_sent",
  INVOICE_PAID: "invoice_paid",

  // Estimates
  ESTIMATE_CREATED: "estimate_created",
  ESTIMATE_UPDATED: "estimate_updated",
  ESTIMATE_DELETED: "estimate_deleted",
  ESTIMATE_SENT: "estimate_sent",
  ESTIMATE_ACCEPTED: "estimate_accepted",
  ESTIMATE_REJECTED: "estimate_rejected",

  // Bookings
  BOOKING_CREATED: "booking_created",
  BOOKING_UPDATED: "booking_updated",
  BOOKING_DELETED: "booking_deleted",
  BOOKING_COMPLETED: "booking_completed",
  BOOKING_CANCELLED: "booking_cancelled",

  // Tasks
  TASK_CREATED: "task_created",
  TASK_UPDATED: "task_updated",
  TASK_DELETED: "task_deleted",
  TASK_ASSIGNED: "task_assigned",
  TASK_COMPLETED: "task_completed",

  // Services & Products
  SERVICE_CREATED: "service_created",
  SERVICE_UPDATED: "service_updated",
  SERVICE_DELETED: "service_deleted",
  PRODUCT_CREATED: "product_created",
  PRODUCT_UPDATED: "product_updated",
  PRODUCT_DELETED: "product_deleted",

  // Expenses
  EXPENSE_CREATED: "expense_created",
  EXPENSE_UPDATED: "expense_updated",
  EXPENSE_DELETED: "expense_deleted",

  // Settings
  SETTINGS_UPDATED: "settings_updated",
  SECURITY_SETTINGS_UPDATED: "security_settings_updated",
  ROLES_UPDATED: "roles_updated",
  BRANDING_UPDATED: "branding_updated",

  // Data
  DATA_EXPORTED: "data_exported",
  DATA_IMPORTED: "data_imported",

  // Subscription
  SUBSCRIPTION_CREATED: "subscription_created",
  SUBSCRIPTION_UPDATED: "subscription_updated",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",

  // Session
  SESSION_TERMINATED: "session_terminated",
  ALL_SESSIONS_TERMINATED: "all_sessions_terminated",
};

/**
 * Get action label for display
 */
export function getActionLabel(action) {
  const labels = {
    [AUDIT_ACTIONS.LOGIN]: "User logged in",
    [AUDIT_ACTIONS.LOGOUT]: "User logged out",
    [AUDIT_ACTIONS.LOGIN_FAILED]: "Login attempt failed",
    [AUDIT_ACTIONS.PASSWORD_CHANGED]: "Password changed",
    [AUDIT_ACTIONS.PASSWORD_RESET_REQUESTED]: "Password reset requested",
    [AUDIT_ACTIONS.USER_CREATED]: "User account created",
    [AUDIT_ACTIONS.USER_UPDATED]: "User account updated",
    [AUDIT_ACTIONS.USER_DELETED]: "User account deleted",
    [AUDIT_ACTIONS.EMPLOYEE_INVITED]: "Team member invited",
    [AUDIT_ACTIONS.EMPLOYEE_REMOVED]: "Team member removed",
    [AUDIT_ACTIONS.ROLE_CHANGED]: "User role changed",
    [AUDIT_ACTIONS.CUSTOMER_CREATED]: "Customer created",
    [AUDIT_ACTIONS.CUSTOMER_UPDATED]: "Customer updated",
    [AUDIT_ACTIONS.CUSTOMER_DELETED]: "Customer deleted",
    [AUDIT_ACTIONS.INVOICE_CREATED]: "Invoice created",
    [AUDIT_ACTIONS.INVOICE_UPDATED]: "Invoice updated",
    [AUDIT_ACTIONS.INVOICE_DELETED]: "Invoice deleted",
    [AUDIT_ACTIONS.INVOICE_SENT]: "Invoice sent",
    [AUDIT_ACTIONS.INVOICE_PAID]: "Invoice marked as paid",
    [AUDIT_ACTIONS.ESTIMATE_CREATED]: "Estimate created",
    [AUDIT_ACTIONS.ESTIMATE_UPDATED]: "Estimate updated",
    [AUDIT_ACTIONS.ESTIMATE_DELETED]: "Estimate deleted",
    [AUDIT_ACTIONS.ESTIMATE_SENT]: "Estimate sent",
    [AUDIT_ACTIONS.ESTIMATE_ACCEPTED]: "Estimate accepted by customer",
    [AUDIT_ACTIONS.ESTIMATE_REJECTED]: "Estimate rejected by customer",
    [AUDIT_ACTIONS.BOOKING_CREATED]: "Booking created",
    [AUDIT_ACTIONS.BOOKING_UPDATED]: "Booking updated",
    [AUDIT_ACTIONS.BOOKING_DELETED]: "Booking deleted",
    [AUDIT_ACTIONS.BOOKING_COMPLETED]: "Booking completed",
    [AUDIT_ACTIONS.BOOKING_CANCELLED]: "Booking cancelled",
    [AUDIT_ACTIONS.TASK_CREATED]: "Task created",
    [AUDIT_ACTIONS.TASK_UPDATED]: "Task updated",
    [AUDIT_ACTIONS.TASK_DELETED]: "Task deleted",
    [AUDIT_ACTIONS.TASK_ASSIGNED]: "Task assigned",
    [AUDIT_ACTIONS.TASK_COMPLETED]: "Task completed",
    [AUDIT_ACTIONS.SERVICE_CREATED]: "Service created",
    [AUDIT_ACTIONS.SERVICE_UPDATED]: "Service updated",
    [AUDIT_ACTIONS.SERVICE_DELETED]: "Service deleted",
    [AUDIT_ACTIONS.PRODUCT_CREATED]: "Product created",
    [AUDIT_ACTIONS.PRODUCT_UPDATED]: "Product updated",
    [AUDIT_ACTIONS.PRODUCT_DELETED]: "Product deleted",
    [AUDIT_ACTIONS.EXPENSE_CREATED]: "Expense recorded",
    [AUDIT_ACTIONS.EXPENSE_UPDATED]: "Expense updated",
    [AUDIT_ACTIONS.EXPENSE_DELETED]: "Expense deleted",
    [AUDIT_ACTIONS.SETTINGS_UPDATED]: "Settings updated",
    [AUDIT_ACTIONS.SECURITY_SETTINGS_UPDATED]: "Security settings updated",
    [AUDIT_ACTIONS.ROLES_UPDATED]: "Role permissions updated",
    [AUDIT_ACTIONS.BRANDING_UPDATED]: "Branding settings updated",
    [AUDIT_ACTIONS.DATA_EXPORTED]: "Data exported",
    [AUDIT_ACTIONS.DATA_IMPORTED]: "Data imported",
    [AUDIT_ACTIONS.SUBSCRIPTION_CREATED]: "Subscription created",
    [AUDIT_ACTIONS.SUBSCRIPTION_UPDATED]: "Subscription updated",
    [AUDIT_ACTIONS.SUBSCRIPTION_CANCELLED]: "Subscription cancelled",
    [AUDIT_ACTIONS.SESSION_TERMINATED]: "Session terminated",
    [AUDIT_ACTIONS.ALL_SESSIONS_TERMINATED]: "All sessions terminated",
  };

  return labels[action] || action;
}

/**
 * Get action category for filtering
 */
export function getActionCategory(action) {
  if (action.startsWith("login") || action.startsWith("logout") || action.includes("password") || action.includes("session")) {
    return "authentication";
  }
  if (action.includes("user") || action.includes("employee") || action.includes("role")) {
    return "users";
  }
  if (action.includes("customer")) return "customers";
  if (action.includes("invoice")) return "invoices";
  if (action.includes("estimate")) return "estimates";
  if (action.includes("booking")) return "bookings";
  if (action.includes("task")) return "tasks";
  if (action.includes("service") || action.includes("product")) return "catalog";
  if (action.includes("expense")) return "expenses";
  if (action.includes("settings") || action.includes("roles") || action.includes("branding")) return "settings";
  if (action.includes("data")) return "data";
  if (action.includes("subscription")) return "billing";
  return "other";
}

/**
 * Log an audit event
 */
export async function logAuditEvent(businessId, action, userId, userEmail, details = {}) {
  if (!businessId) {
    console.warn("Cannot log audit event: no businessId");
    return null;
  }

  try {
    const deviceInfo = getDeviceInfo();

    const auditEntry = {
      action,
      actionLabel: getActionLabel(action),
      category: getActionCategory(action),
      userId: userId || "system",
      userEmail: userEmail || "system",
      details: typeof details === "string" ? details : JSON.stringify(details),
      metadata: typeof details === "object" ? details : {},
      device: deviceInfo.device,
      browser: deviceInfo.browser,
      timestamp: serverTimestamp(),
    };

    const docRef = await addDoc(
      collection(db, "businesses", businessId, "auditLogs"),
      auditEntry
    );

    return docRef.id;
  } catch (error) {
    console.error("Error logging audit event:", error);
    return null;
  }
}

/**
 * Get audit logs with pagination and filtering
 */
export async function getAuditLogs(businessId, options = {}) {
  if (!businessId) return { logs: [], hasMore: false };

  const {
    pageSize = 50,
    lastDoc = null,
    category = null,
    userId = null,
    startDate = null,
    endDate = null,
  } = options;

  try {
    let q = query(
      collection(db, "businesses", businessId, "auditLogs"),
      orderBy("timestamp", "desc"),
      limit(pageSize + 1) // Get one extra to check if there are more
    );

    // Apply filters
    if (category) {
      q = query(q, where("category", "==", category));
    }

    if (userId) {
      q = query(q, where("userId", "==", userId));
    }

    if (startDate) {
      q = query(q, where("timestamp", ">=", startDate));
    }

    if (endDate) {
      q = query(q, where("timestamp", "<=", endDate));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const logs = [];
    let lastVisible = null;

    snapshot.docs.slice(0, pageSize).forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
      });
      lastVisible = doc;
    });

    return {
      logs,
      lastDoc: lastVisible,
      hasMore: snapshot.docs.length > pageSize,
    };
  } catch (error) {
    console.error("Error fetching audit logs:", error);
    return { logs: [], hasMore: false };
  }
}

/**
 * Get recent audit logs (simplified)
 */
export async function getRecentAuditLogs(businessId, count = 50) {
  if (!businessId) return [];

  try {
    const q = query(
      collection(db, "businesses", businessId, "auditLogs"),
      orderBy("timestamp", "desc"),
      limit(count)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching recent audit logs:", error);
    return [];
  }
}

/**
 * Get audit log statistics
 */
export async function getAuditStats(businessId, days = 30) {
  if (!businessId) return null;

  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const q = query(
      collection(db, "businesses", businessId, "auditLogs"),
      where("timestamp", ">=", startDate),
      orderBy("timestamp", "desc")
    );

    const snapshot = await getDocs(q);

    const stats = {
      total: snapshot.size,
      byCategory: {},
      byAction: {},
      byUser: {},
    };

    snapshot.docs.forEach((doc) => {
      const data = doc.data();

      // Count by category
      const category = data.category || "other";
      stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

      // Count by action
      stats.byAction[data.action] = (stats.byAction[data.action] || 0) + 1;

      // Count by user
      const userKey = data.userEmail || data.userId;
      stats.byUser[userKey] = (stats.byUser[userKey] || 0) + 1;
    });

    return stats;
  } catch (error) {
    console.error("Error fetching audit stats:", error);
    return null;
  }
}

/**
 * Hook-friendly function to log with current user context
 */
export function createAuditLogger(businessId, currentUser) {
  return async (action, details = {}) => {
    return logAuditEvent(
      businessId,
      action,
      currentUser?.uid,
      currentUser?.email,
      details
    );
  };
}

export default {
  AUDIT_ACTIONS,
  getActionLabel,
  getActionCategory,
  logAuditEvent,
  getAuditLogs,
  getRecentAuditLogs,
  getAuditStats,
  createAuditLogger,
};
